<script setup lang="ts">
// Urutan import ini WAJIB: main.css membawa Tailwind + Nuxt UI + seluruh utility
// semantik; v3.css hanya melapisi ulang primitif `--ca-*` pada subtree `.ca-v3`.
// Dibalik, warna bawaan main.css yang akan menang.
// gerak.css dimuat PALING AKHIR: ia memakai token warna milik v3.css
// (--ca-teal, --ca-cta-bg) dan sakelar reduced-motion-nya harus menang atas
// aturan mana pun di atasnya.
import '~/assets/css/main.css'
import '~/assets/css/v3.css'
import '~/assets/css/gerak.css'

/**
 * Layout UTAMA situs — tema "Liquid CoreAsia" (putih + gold + teal).
 *
 * Ini bekas layout prototipe `v3`, dipromosikan menjadi `default` setelah
 * pemilik memilihnya. Desain lama masih dapat ditelusuri di /legacy/* dengan
 * layout `legacy`; prototipe biru /v2 sudah dihapus.
 *
 * Nama berkas v3.css / gerak.css sengaja DIPERTAHANKAN supaya riwayat git
 * tetap dapat diikuti — isinya, bukan namanya, yang menjadi sumber kebenaran.
 */
// Preload font merek HANYA di layout default (bukan di `ringan`): di sini font
// ada di jalur kritis desain, jadi memuluskan swap sepadan. `crossorigin` wajib
// walau same-origin — permintaan font selalu mode CORS anonim; tanpa atribut ini
// preload-nya tak dipakai ulang dan berkas terunduh dua kali.
useHead({
  link: [
    {
      rel: 'preload',
      href: '/fonts/plus-jakarta-sans-latin.woff2',
      as: 'font',
      type: 'font/woff2',
      crossorigin: ''
    }
  ]
})

const colorMode = useColorMode()
const menuTerbuka = ref(false)
const route = useRoute()
const { adaWa, adaEmail, tautanWa, tautanEmail, adaNama, namaBadanUsaha } = useIdentitas()

watch(() => route.fullPath, () => (menuTerbuka.value = false))

const tautan = [
  { ke: '/#alat', label: 'Semua alat' },
  { ke: '/berkaskilat', label: 'BerkasKilat' },
  { ke: '/#harga', label: 'Harga' },
  // Satu-satunya jalan ke ebook dari menu. Sebelum ini buku hanya ditautkan
  // dari /harga — halaman yang tidak ada di menu mana pun — sehingga dari
  // beranda dan seluruh halaman alat, tidak ada satu tautan pun ke buku.
  { ke: '/buku/melek-komputer', label: 'Ebook' },
  { ke: '/#bantuan', label: 'Bantuan' }
]

const modeBerikut = computed(() => (colorMode.value === 'dark' ? 'light' : 'dark'))
function gantiTema() {
  colorMode.preference = modeBerikut.value
}

// Gulir-mulus untuk tautan menu yang menuju anchor (Semua alat → #alat,
// Harga → #harga, Bantuan → #bantuan), termasuk perbaikan untuk section
// ber-content-visibility. Dipakai juga oleh layout `ringan` — lihat
// composables/useGulirAnchor.ts untuk detail lengkap kenapa dan bagaimana.
useGulirAnchor()

const tahunIni = new Date().getFullYear()
</script>

<template>
  <UApp>
    <div class="ca-root ca-v3 min-h-dvh flex flex-col">
      <NuxtRouteAnnouncer />

      <CaGlassBar posisi="atas">
        <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
          <div class="flex items-center gap-3 h-16">
            <!-- min-h-11: lockup juga sebuah tautan, jadi ia ikut aturan tap target 44px. -->
            <NuxtLink
              to="/"
              class="ca-focus shrink-0 min-w-0 inline-flex items-center min-h-11"
              aria-label="CoreAsia Beres — beranda"
            >
              <CaLockup />
            </NuxtLink>

            <nav class="hidden lg:flex items-center gap-1 ms-4">
              <CaButton v-for="t in tautan" :key="t.ke" variant="ghost" size="sm" :to="t.ke">
                {{ t.label }}
              </CaButton>
            </nav>

            <div class="ms-auto flex items-center gap-2">
              <CaButton
                variant="ghost"
                size="md"
                class="w-11 px-0!"
                :icon="colorMode.value === 'dark' ? 'i-lucide-sun' : 'i-lucide-moon'"
                :aria-label="`Ganti ke tema ${modeBerikut === 'dark' ? 'gelap' : 'terang'}`"
                @click="gantiTema"
              />
              <CaButton class="hidden sm:inline-flex" variant="secondary" size="md" to="/redeem">
                Tukar kode
              </CaButton>
              <!-- Label ikut KEADAAN, bukan tetap "Buka menu": tombol yang
                   masih mengaku "Buka menu" padahal menunya sudah terbuka
                   memberi tahu pengguna pembaca layar hal yang salah.
                   aria-expanded menyampaikan keadaan itu secara terprogram,
                   aria-haspopup menjelaskan yang akan muncul adalah dialog. -->
              <CaButton
                class="lg:hidden w-11 px-0!"
                variant="ghost"
                size="md"
                icon="i-lucide-menu"
                :aria-label="menuTerbuka ? 'Tutup menu' : 'Buka menu'"
                aria-haspopup="dialog"
                :aria-expanded="menuTerbuka"
                @click="menuTerbuka = !menuTerbuka"
              />
            </div>
          </div>
        </div>
      </CaGlassBar>

      <main class="flex-1">
        <slot />
      </main>

      <footer class="ca-tunda border-t border-line bg-surface-2 mt-20">
        <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6 py-12">
          <div class="grid gap-9 sm:grid-cols-2 lg:grid-cols-4">
            <div class="sm:col-span-2 lg:col-span-1">
              <CaLockup />
              <p class="ca-caption text-muted mt-3 leading-relaxed max-w-xs">
                Alat praktis untuk urusan berkas dan kontak. Bayar sekali, tanpa langganan.
              </p>
            </div>

            <div class="flex flex-col gap-1">
              <p class="text-sm font-bold text-ink mb-1">Alat gratis</p>
              <CaButton variant="link" size="sm" to="/kompres-foto">Kompres foto</CaButton>
              <CaButton variant="link" size="sm" to="/kompres-pdf">Kompres PDF</CaButton>
            </div>

            <div class="flex flex-col gap-1">
              <p class="text-sm font-bold text-ink mb-1">Produk berbayar</p>
              <CaButton variant="link" size="sm" to="/berkaskilat">BerkasKilat</CaButton>
              <CaButton variant="link" size="sm" to="/rapikontak">RapiKontak</CaButton>
              <CaButton variant="link" size="sm" to="/buku/melek-komputer">Ebook Melek Komputer</CaButton>
              <CaButton variant="link" size="sm" to="/#harga">Harga</CaButton>
            </div>

            <div class="flex flex-col gap-1">
              <p class="text-sm font-bold text-ink mb-1">Perusahaan</p>
              <CaButton variant="link" size="sm" to="/syarat">Syarat layanan</CaButton>
              <CaButton variant="link" size="sm" to="/privasi">Kebijakan privasi</CaButton>
              <CaButton variant="link" size="sm" to="/refund">Ketentuan refund</CaButton>
              <!-- Kanal kontak hanya muncul bila datanya benar-benar ada.
                   Lihat composables/useIdentitas.ts — situs ini tidak pernah
                   memasang nomor atau email karangan. -->
              <CaButton
                v-if="adaWa"
                variant="link"
                size="sm"
                :href="tautanWa({ konteks: 'Footer' })"
              >WhatsApp</CaButton>
              <CaButton v-if="adaEmail" variant="link" size="sm" :href="tautanEmail()">Email</CaButton>
            </div>
          </div>

          <CaSeparator class="my-9" />

          <p class="ca-caption text-muted max-w-3xl leading-relaxed">
            Batas ukuran berkas ditetapkan oleh masing-masing instansi dan bisa berubah
            sewaktu-waktu. Selalu periksa kembali pengumuman resmi instansi tujuan sebelum
            mengunggah.
          </p>
          <p class="ca-caption text-muted mt-3">
            <span v-if="adaNama">{{ namaBadanUsaha }} · </span>tools.coreasia.id · {{ tahunIni }}
          </p>
        </div>
      </footer>

      <CaDialog v-model:open="menuTerbuka" judul="Menu">
        <nav class="flex flex-col gap-1.5">
          <CaButton
            v-for="t in tautan"
            :key="t.ke"
            variant="ghost"
            size="lg"
            block
            class="justify-start!"
            :to="t.ke"
          >{{ t.label }}</CaButton>
          <CaSeparator class="my-2" />
          <CaButton variant="secondary" size="lg" block to="/redeem">Tukar kode aktivasi</CaButton>
        </nav>
      </CaDialog>
    </div>
  </UApp>
</template>
